'use client';

import { useCallback, useEffect, useState } from 'react';

import type {
    ContractProduct,
    DrawerState,
} from '@/components/organisms/ContractDetailsDrawer/ContractDetailsDrawer';
import { contractEndpoints } from '../api/contract';

interface UseContractInfoOptions {
    dealerRefId?: string | number | null;
    enabled: boolean;
}

interface UseContractInfoResult {
    state: DrawerState;
    contractedProducts: ContractProduct[];
    error?: { title: string; message: string };
    refetch: () => void;
}

/**
 * Fetches contract info for the given dealer when `enabled` is true
 * (typically when the drawer opens). Manages loading / empty / error / ready
 * state for the ContractDetailsDrawer.
 */
export const useContractInfo = ({ dealerRefId, enabled }: UseContractInfoOptions): UseContractInfoResult => {
    const [state, setState] = useState<DrawerState>('loading');
    const [contractedProducts, setContractedProducts] = useState<ContractProduct[]>([]);
    const [error, setError] = useState<{ title: string; message: string } | undefined>(undefined);
    const [reloadKey, setReloadKey] = useState(0);

    const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        if (!enabled) return;

        let cancelled = false;
        setState('loading');
        setError(undefined);

        if (!dealerRefId) {
            setState('empty');
            setContractedProducts([]);
            return;
        }

        contractEndpoints
            .getContractInfo(dealerRefId)
            .then((res) => {
                if (cancelled) return;
                if (!res.contractedProducts.length) {
                    setContractedProducts([]);
                    setState('empty');
                    return;
                }
                setContractedProducts(res.contractedProducts);
                setState('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('[useContractInfo] failed to fetch contract info', err);
                setError({
                    title: 'Unable to load contract details',
                    message: 'Something went wrong while loading this dealer\'s contract. Please try again.',
                });
                setState('error');
            });

        return () => {
            cancelled = true;
        };
    }, [enabled, dealerRefId, reloadKey]);

    return { state, contractedProducts, error, refetch };
};
